import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Mail,
  Phone,
  Clock,
  Trash2,
  ChevronDown,
  ChevronUp,
  Filter,
  MapPin,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";

const statusColors = {
  new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  contacted: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  archived: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

export default function QuotesPage() {
  const { authAxios } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const res = await authAxios.get("/quotes");
      setQuotes(res.data);
    } catch (err) {
      console.error("Failed to fetch quotes", err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await authAxios.patch(`/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await authAxios.delete(`/quotes/${id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      console.error("Failed to delete quote", err);
    }
  };

  const filtered = filterStatus === "all" ? quotes : quotes.filter((q) => q.status === filterStatus);

  const statusCounts = {
    all: quotes.length,
    new: quotes.filter((q) => q.status === "new").length,
    contacted: quotes.filter((q) => q.status === "contacted").length,
    completed: quotes.filter((q) => q.status === "completed").length,
    archived: quotes.filter((q) => q.status === "archived").length,
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Quote Requests</h1>
        <p className="text-gray-500 text-sm">Manage incoming quote enquiries</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {["all", "new", "contacted", "completed", "archived"].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`p-4 rounded-xl border text-left transition-all duration-200 ${
              filterStatus === s
                ? "bg-[#DC2626]/10 border-[#DC2626]/30"
                : "bg-[#111118] border-white/[0.04] hover:border-white/10"
            }`}
          >
            <p className="text-2xl font-bold text-white">{statusCounts[s]}</p>
            <p className="text-gray-500 text-xs capitalize mt-1">{s}</p>
          </button>
        ))}
      </div>

      {/* Quote List */}
      {loading ? (
        <div className="text-gray-500 text-center py-20">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <MessageSquare className="h-12 w-12 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-500">No quote requests yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((quote) => (
            <div
              key={quote.id}
              className="bg-[#111118] border border-white/[0.04] rounded-xl overflow-hidden"
            >
              {/* Header row */}
              <div
                className="flex items-center justify-between p-5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                onClick={() => setExpandedId(expandedId === quote.id ? null : quote.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="shrink-0">
                    <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium border ${statusColors[quote.status]}`}>
                      {quote.status}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm truncate">{quote.name}</p>
                    <p className="text-gray-500 text-xs truncate">{quote.service}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-gray-600 text-xs hidden md:block">
                    {new Date(quote.created_at).toLocaleDateString()}
                  </span>
                  {expandedId === quote.id ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === quote.id && (
                <div className="border-t border-white/[0.04] p-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#DC2626]" />
                      <span className="text-gray-300 text-sm">{quote.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-[#DC2626]" />
                      <span className="text-gray-300 text-sm">{quote.phone || "Not provided"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#DC2626]" />
                      <span className="text-gray-300 text-sm">{quote.location || "Not provided"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#DC2626]" />
                      <span className="text-gray-300 text-sm">
                        {new Date(quote.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Message</p>
                    <p className="text-gray-300 text-sm leading-relaxed bg-[#0A0A0F] rounded-lg p-4">
                      {quote.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <Select
                      defaultValue={quote.status}
                      onValueChange={(val) => updateStatus(quote.id, val)}
                    >
                      <SelectTrigger className="w-40 bg-[#0A0A0F] border-white/[0.06] text-white h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111118] border-white/[0.08]">
                        {["new", "contacted", "completed", "archived"].map((s) => (
                          <SelectItem key={s} value={s} className="text-gray-300 capitalize text-xs focus:bg-[#DC2626]/10">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteQuote(quote.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
